import { rpcClient } from '@/api/rpc/client';

export interface UploadInlineImageParams {
  file: File;
  /** Association entity type the document is stored under (scopes authorization). */
  entityType: string;
  /** Association entity id (e.g. the organization or user the image belongs to). */
  entityId: string;
}

/**
 * Uploads an image through the generic `core.documents` flow and returns a
 * renderable `src` (signed URL, for editor preview) plus the durable
 * `documentId` (which the send path resolves to an inline `cid:` attachment).
 * Shared by the email-signature editor and the inline-image editor uploader so
 * every editor uses one upload path.
 */
export async function uploadInlineImage({
  file,
  entityType,
  entityId,
}: UploadInlineImageParams): Promise<{ src: string; documentId: string }> {
  const contentType = file.type || 'application/octet-stream';
  const { documentId, uploadUrl } = await rpcClient.core.documents.getUploadUrl(
    {
      entityType,
      entityId,
      fileName: file.name,
      contentType,
    },
  );

  const headers: Record<string, string> = { 'Content-Type': contentType };
  if (uploadUrl.includes('.blob.core.windows.net')) {
    headers['x-ms-blob-type'] = 'BlockBlob';
  }
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers,
  });
  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload image: ${uploadResponse.statusText}`);
  }

  await rpcClient.core.documents.confirmUpload({
    documentId,
    entityType,
    entityId,
    fileSizeBytes: file.size,
  });

  const { signedUrl } = await rpcClient.core.documents.getPreview({
    documentId,
  });
  return { src: signedUrl, documentId };
}
